import React, { useEffect, useRef, useState } from 'react';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { subscriptionHabitationApi } from '@/api/stripeApi';
import type { StripeElementsOptions } from '@stripe/stripe-js';
import styled from '@emotion/styled';
import { Button, Grid} from '@mui/material';
import { Address, Roommate } from '../types';
import { setContractReference, setStripeConfigType, setSubscriptionId } from '../habitationSlice';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import { trackDatadogError } from '@/services/datadog';
import ErrorBoundary from '../../../components/ErrorBoundary/ErrorBoundary';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || '');

const ButtonStyled = styled(Button)`
  background-color: #2f6975;
  color: white;
  border-radius: 30px;
  width: 150px;
  height: 35px;
  font-weight: 400;
  font-size: 15px;
  &:hover: {
    background-color: #26555f;
  }
  @media (max-width: 600px) {
      width: 100%;
      font-size: 13px;
      height: 30px;
  }
`;

interface HomeInsurancePaymentWrapperProps {
  paymentType: string;
  housingType: 'room' | 'full';
  numberOfRooms: number;
  startDate: string | null;
  address: Address;
  floorLevel: number;
  roommates?: Roommate[];
}

export function HomeInsurancePaymentWrapper({
  paymentType,
  housingType,
  numberOfRooms,
  startDate,
  address,
  floorLevel,
  roommates = [],
}: HomeInsurancePaymentWrapperProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const contractReference = useAppSelector((state) => state.habitation.contractReference);
  const hasFetchedRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    // Marquer le composant comme monté
    isMountedRef.current = true;

    return () => {
      // Nettoyer au démontage
      isMountedRef.current = false;
      // Reset le flag pour permettre un nouvel appel si la modale est réouverte
      hasFetchedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // Protection contre les appels multiples (StrictMode + réouverture modale)
    if (hasFetchedRef.current) return;

    hasFetchedRef.current = true;

    if( !startDate || !address || !paymentType || !housingType || !numberOfRooms) {
      console.error('Missing required fields for subscriptionHabitationApi');
      return;
    }

    const floorLevelMap = ['ground', 'middle', 'top'];

    let payload = {
        paymentType,
        housingType,
        numberOfRooms,
        startDate: startDate,
        address,
        floorLevel: floorLevelMap[floorLevel] || 'ground',
        roommates: roommates,
        contractReference: contractReference || '',
      };
    if(housingType === 'room') {
        payload = {
          paymentType,
          housingType,
          numberOfRooms: 1,
          startDate: startDate,
          address,
          floorLevel: floorLevelMap[floorLevel] || 'ground',
          roommates: [],
          contractReference: contractReference || '',
        };
      }

    subscriptionHabitationApi(payload)
      .then(res => {
        // Vérifier que le composant est toujours monté avant de mettre à jour le state
        if (!isMountedRef.current) return;

        if (res.data.clientSecret) {
          dispatch(setContractReference(res.data.contractReference));
          // Le back renvoie toujours 'payment_intent' depuis la refacto Stripe.
          dispatch(setStripeConfigType('payment_intent'));
          dispatch(setSubscriptionId(res.data.subscriptionId));
          // Persist contractReference in localStorage to survive Stripe redirect
          localStorage.setItem('habitation_contractReference', res.data.contractReference);
          setClientSecret(res.data.clientSecret);
        }
      })
      .catch(err => {
        if (!isMountedRef.current) return;
        console.error('Erreur récupération clientSecret:', err);
      });
  }, [contractReference, startDate, address, paymentType, housingType, numberOfRooms, floorLevel, dispatch, roommates]);


  if (!clientSecret) return <Grid container alignContent={'center'} justifyContent={'center'} textAlign={'center'}><Grid item xs={12} ><CircularProgress size={22} color="inherit" /></Grid></Grid>;

  const options: StripeElementsOptions = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      labels: 'floating',
    },
  };

  return (
    <ErrorBoundary variant="payment">
      <Elements key={clientSecret} stripe={stripePromise} options={options}>
        <HomeInsurancePaymentForm />
      </Elements>
    </ErrorBoundary>
  );
}

 function HomeInsurancePaymentForm() {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  // `elements` existe dès que <Elements> est monté ; il ne dit rien du montage
  // effectif de l'iframe. Sans cette garde, un clic sur Payer trop tôt part en
  // IntegrationError « elements should have a mounted Payment Element ».
  const [ready, setReady] = useState(false);
  const { t } = useTranslation(['habitation', 'common']);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setErrorMessage('');

    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/insurance/housing/success-payment`,
        },
      });

      if (result.error) {
        setErrorMessage(
          result.error.message || t('payment.start-failed', { ns: 'common' }),
        );
        trackDatadogError(
          new Error(result.error.message || 'Stripe confirmPayment failed'),
          {
            action: 'habitation_stripe_confirm_payment_failed',
            stripeErrorType: result.error.type,
            stripeErrorCode: result.error.code,
          },
        );
      }
    } catch (err) {
      // confirmPayment LÈVE ses IntegrationError au lieu de les retourner dans
      // result.error. Sans ce catch, la promesse rejette : setLoading(false)
      // n'est jamais atteint et le bouton reste bloqué en spinner.
      setErrorMessage(t('payment.start-failed', { ns: 'common' }));
      trackDatadogError(err as Error, {
        action: 'habitation_stripe_confirm_payment_threw',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-md mx-auto border rounded-lg shadow bg-white"
    >
      <div className="mb-4">
        {/* link: 'never' désactive le wallet Stripe Link (friction email/tel inutile,
            l'email est déjà attaché au customer côté back). Cast nécessaire car les
            types de @stripe/stripe-js@1.17 ne déclarent pas encore `link` dans
            PaymentWalletsOption, mais le runtime Stripe.js (CDN, à jour) le supporte. */}
        <PaymentElement
          options={{ layout: 'tabs', wallets: { link: 'never' } as never }}
          onReady={() => setReady(true)}
        />
      </div>

      {errorMessage && (
        <div className="text-red-600 text-sm mb-2">{errorMessage}</div>
      )}
      <ButtonStyled
        type="submit"
        variant="contained"
        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded transition-colors duration-200"
        disabled={!stripe || !ready || loading}
      >
        {loading ? <CircularProgress size={22} color="inherit" /> : t('step5.subscribe')}
      </ButtonStyled>
    </form>
  );
}
